import { useMemo, useRef, useState } from 'react';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import WaterfallChartOutlinedIcon from '@mui/icons-material/WaterfallChartOutlined';
import { Box, CardActionArea, Divider, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { companiesAction, fundsAction } from 'common/actions';
import {
  companySummaryUrl,
  firmDocumentsUrl,
  firmHomeUrl,
  firmIntelligenceUrl,
  firmValuationsUrl,
  firmWaterfallUrl,
} from 'common/config/urls';
import { useStore } from 'common/store';
import { FirmCompany } from 'common/types/company';
import { Firm, FIRM_LIST_STATUSES } from 'common/types/firm';
import { UseStoreValues } from 'common/types/store';
import { Input, InputAdornment as UiInputAdornment } from 'components/ui';
import { getCompanyAliasLabel, getCompanyAliasPath } from 'layouts/FirmBaseLayout/companyAliasSearch';
import { ACCENT, BRAND, YELLOW } from 'theme/prismDesignTokens';
import { getInitials } from 'utilities/getInitials';
import {
  CompanyCell,
  CompanyGrid,
  CompanyHeading,
  CompanyListRoot,
  FeatureCard,
  FeatureGrid,
  FirmIconAvatar,
  FirmIconWrapper,
  FirmRibbon,
  HomeRoot,
  LetterGroup,
  LetterHeaderBox,
} from './Home.styles';

const FEATURE_CARDS = [
  {
    key: 'intelligence',
    label: 'Intelligence',
    description: 'Customizable summaries of the entire portfolio.',
    icon: <AssessmentOutlinedIcon fontSize="medium" />,
    color: BRAND[100],
    iconColor: BRAND[500],
    getUrl: firmIntelligenceUrl,
  },
  {
    key: 'valuations',
    label: 'Valuations',
    description: 'Find the details and bulk actions on all of the latest valuations.',
    icon: <ShowChartOutlinedIcon fontSize="medium" />,
    color: ACCENT[100],
    iconColor: ACCENT[600],
    getUrl: firmValuationsUrl,
  },
  {
    key: 'waterfalls',
    label: 'Waterfalls',
    description: 'Run a waterfall on any company\'s cap table to quickly find the distributions.',
    icon: <WaterfallChartOutlinedIcon fontSize="medium" />,
    color: BRAND[100],
    iconColor: BRAND[500],
    getUrl: firmWaterfallUrl,
  },
  {
    key: 'documents',
    label: 'Documents',
    description: 'Quickly find the documents that were used for each valuation.',
    icon: <DescriptionOutlinedIcon fontSize="medium" />,
    color: YELLOW[100],
    iconColor: YELLOW[600],
    getUrl: firmDocumentsUrl,
  },
];

const ALPHA_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const GROUP_CHARS = [...ALPHA_CHARS, '#'];

type FirmUrlBuilder = (firmSlug: string) => string;

interface GridEntry {
  getUrl: FirmUrlBuilder;
  key: string;
  label: string;
  sortName: string;
}

const toCompanyEntry = (company: FirmCompany): GridEntry => ({
  getUrl: firmSlug => companySummaryUrl(firmSlug, company.company_slug),
  key: company.company_slug,
  label: company.company_name,
  sortName: company.company_name,
});

// Every company when not searching; otherwise matching companies plus matching aliases, as the navbar does.
const buildGridEntries = (companies: FirmCompany[], query: string): GridEntry[] => {
  const q = query.trim().toLowerCase();
  if (!q) {
    return companies.map(toCompanyEntry);
  }

  const matches = (name: string) => name.toLowerCase().includes(q);
  return companies.flatMap(company => [
    ...(matches(company.company_name) ? [toCompanyEntry(company)] : []),
    ...(company.aliases ?? [])
      .filter(alias => matches(alias.name))
      .map(alias => ({
        getUrl: (firmSlug: string) => getCompanyAliasPath(alias, firmSlug, company.company_slug),
        key: `alias:${alias.id}`,
        label: getCompanyAliasLabel(alias, company.company_name),
        sortName: alias.name,
      })),
  ]);
};

const groupAlphabetically = (entries: GridEntry[]) => {
  const groups: Record<string, GridEntry[]> = {};
  [...entries]
    .sort((a, b) => a.sortName.localeCompare(b.sortName))
    .forEach(entry => {
      const firstCharacter = entry.sortName.charAt(0).toUpperCase();
      (groups[ALPHA_CHARS.includes(firstCharacter) ? firstCharacter : '#'] ??= []).push(entry);
    });
  return GROUP_CHARS.filter(char => groups[char]).map(char => ({ char, entries: groups[char] }));
};

const FirmIconChip = ({ firm, onClick }: { firm: Firm; onClick: (slug: string) => void }) => {
  const initials = getInitials({ name: firm.name });

  return (
    <FirmIconWrapper onClick={() => onClick(firm.slug)} title={firm.name}>
      {firm.icon_url ? (
        <img
          alt={firm.name}
          src={firm.icon_url}
          style={{ display: 'block', height: '100%', objectFit: 'contain', width: '100%' }}
        />
      ) : (
        <FirmIconAvatar variant="rounded">{initials}</FirmIconAvatar>
      )}
    </FirmIconWrapper>
  );
};

const HomePage = () => {
  const { firmSlugParam } = useParams<{ firmSlugParam: string }>();
  const navigate = useNavigate();
  const [{ firmList, firmListStatus, firmInfo, companyList, user }, dispatch] = useStore() as UseStoreValues;
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isStaff = Boolean(user?.is_staff);

  const resolvedFirmList = useMemo<Firm[]>(
    () => (firmListStatus === FIRM_LIST_STATUSES.READY && Array.isArray(firmList) ? (firmList as Firm[]) : []),
    [firmList, firmListStatus]
  );

  const letterGroups = useMemo(
    () => groupAlphabetically(buildGridEntries((companyList ?? []) as FirmCompany[], query)),
    [companyList, query]
  );

  const handleFirmClick = (slug: string) => {
    if (slug !== firmSlugParam) {
      dispatch(companiesAction.setCompanyList(null));
      dispatch(fundsAction.setFundList(null));
    }
    navigate(firmHomeUrl(slug));
  };

  const handleFirmPageClick = (getUrl: FirmUrlBuilder) => {
    if (firmSlugParam) {
      navigate(getUrl(firmSlugParam));
    }
  };

  return (
    <HomeRoot>
      {/* Firm ribbon - is_staff only */}
      {isStaff && resolvedFirmList.length > 0 && (
        <FirmRibbon>
          {resolvedFirmList.map(firm => (
            <FirmIconChip key={firm.id} firm={firm} onClick={handleFirmClick} />
          ))}
        </FirmRibbon>
      )}

      {/* Feature cards */}
      <FeatureGrid>
        {FEATURE_CARDS.map(card => (
          <FeatureCard key={card.key} elevation={0} $bgColor={card.color}>
            <CardActionArea
              onClick={() => handleFirmPageClick(card.getUrl)}
              sx={{ height: '100%', p: 2.5 }}>
              <Box sx={{ color: card.iconColor, mb: 1.5 }}>{card.icon}</Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {card.label}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {card.description}
              </Typography>
            </CardActionArea>
          </FeatureCard>
        ))}
      </FeatureGrid>

      {/* Search */}
      <Input
        autoFocus
        inputRef={inputRef}
        placeholder="Search companies..."
        value={query}
        onChange={e => setQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <UiInputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary' }} />
            </UiInputAdornment>
          ),
        }}
        sx={{ mb: 4 }}
      />

      {/* Company grid heading */}
      {firmInfo?.name && (
        <CompanyHeading>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {firmInfo.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Portfolio Companies
          </Typography>
        </CompanyHeading>
      )}

      {/* Alphabetical company grid */}
      {letterGroups.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          {query ? 'No companies match your search.' : 'No companies found.'}
        </Typography>
      ) : (
        <CompanyListRoot>
          {letterGroups.map(({ char, entries }) => (
            <LetterGroup key={char}>
              <LetterHeaderBox>
                <Typography
                  variant="caption"
                  sx={theme => ({
                    color: theme.palette.text.secondary,
                    display: 'block',
                    fontWeight: 500,
                    mb: 0.75,
                  })}>
                  {char}
                </Typography>
                <Divider />
              </LetterHeaderBox>

              <CompanyGrid>
                {entries.map(entry => (
                  <CompanyCell key={entry.key} onClick={() => handleFirmPageClick(entry.getUrl)}>
                    <Typography className="company-name" variant="body2">
                      {entry.label}
                    </Typography>
                  </CompanyCell>
                ))}
              </CompanyGrid>
            </LetterGroup>
          ))}
        </CompanyListRoot>
      )}
    </HomeRoot>
  );
};

export default HomePage;
